import { ReactElement, useId, useMemo, useState } from 'react'
import { formatCents, ProgressBar, SegmentedControl } from '../../../components/SalesReport'
import { AccessReportResponse } from '../../../hooks/access-report'
import { deviceLabel, ORIGIN_LABELS } from '../filters'
import {
  attendanceChips,
  attendanceTone,
  deviceGapRows,
  deviceGapSummary,
  deviceStatus,
  DeviceStatus,
  formatAttendance,
  formatClock,
  formatCount,
  formatDayTime,
  formatRate,
  heatIntensity,
  heatmapColumns,
  HeatmapScale,
} from '../report'
import * as S from './styles'

export function AttendanceValue({ attendance }: { attendance: number | null }): ReactElement {
  if (attendance === null) return <S.Muted>—</S.Muted>
  const text = formatAttendance(attendance)
  return (
    <S.Attendance>
      <ProgressBar
        percentage={attendance * 100}
        track="neutral"
        tone={attendanceTone(attendance)}
        label={`Comparecimento ${text}`}
      />
      <span>{text}</span>
    </S.Attendance>
  )
}

const GAP_PREVIEW = 10

function DeviceGaps({ report }: { report: AccessReportResponse }): ReactElement | null {
  const [open, setOpen] = useState(false)
  const [all, setAll] = useState(false)
  const listId = useId()
  const rows = deviceGapRows(report)
  if (rows.length === 0) return null
  const visible = all ? rows : rows.slice(0, GAP_PREVIEW)

  return (
    <S.Gaps>
      <S.GapToggle type="button" aria-expanded={open} aria-controls={listId} onClick={() => setOpen(!open)}>
        {deviceGapSummary(rows.length)}
        <S.GapAction>{open ? 'ocultar' : 'ver leitores'}</S.GapAction>
      </S.GapToggle>
      {open && (
        <S.GapList id={listId} aria-label="Leitores parados enquanto outros liam">
          {visible.map((row) => (
            <S.GapRow key={row.key}>
              <S.GapDevice title={row.deviceId}>{row.device}</S.GapDevice>
              <S.GapMinutes>{row.minutes}</S.GapMinutes>
              <S.GapWindow>{row.window}</S.GapWindow>
            </S.GapRow>
          ))}
          {rows.length > GAP_PREVIEW && (
            <S.GapMore type="button" onClick={() => setAll(!all)}>
              {all ? 'Mostrar menos' : `Ver todos (${formatCount(rows.length)})`}
            </S.GapMore>
          )}
        </S.GapList>
      )}
    </S.Gaps>
  )
}

export function AttendanceBand({ report }: { report: AccessReportResponse }): ReactElement {
  const { totals, showValues } = report
  const chips = attendanceChips(report)
  const attendance = totals.attendance
  return (
    <S.Band aria-label="Comparecimento">
      <S.Label>Comparecimento</S.Label>
      {attendance === null || totals.issued === null ? (
        <S.Line>
          <span>
            {report.periodFiltered
              ? 'Com filtro de período, o comparecimento não se aplica: quem entrou fora do período não é pendente.'
              : 'O comparecimento depende dos ingressos emitidos e não se aplica com filtro de dispositivo.'}
          </span>
        </S.Line>
      ) : (
        <>
          <S.Percent $tone={attendanceTone(attendance)}>{formatAttendance(attendance)}</S.Percent>
          <ProgressBar
            percentage={attendance * 100}
            label="Comparecimento do evento"
            valueText={`${formatAttendance(attendance)} dos ingressos emitidos entraram`}
            track="neutral"
            tone={attendanceTone(attendance)}
            height={10}
          />
          <S.Line>
            <span>{`${formatCount(totals.entries)} entradas de ${formatCount(totals.issued)} ingressos emitidos`}</span>
            <span>
              {`${formatCount(totals.pending ?? 0)} ainda não entraram`}
              {showValues && totals.unusedRevenueCents != null && ` · ${formatCents(totals.unusedRevenueCents)}`}
            </span>
          </S.Line>
        </>
      )}
      {chips.length > 0 && (
        <S.Chips>
          {chips.map((chip) => (
            <S.Chip key={chip.key} $tone={chip.tone}>
              {chip.text}
            </S.Chip>
          ))}
        </S.Chips>
      )}
      <DeviceGaps report={report} />
    </S.Band>
  )
}

export function OriginBreakdown({ report }: { report: AccessReportResponse }): ReactElement {
  return (
    <S.Card aria-label="Origem da venda">
      <S.CardHead>
        <div>
          <S.CardTitle>Origem da venda</S.CardTitle>
          <S.CardSubtitle>Quanto de cada canal já entrou no evento.</S.CardSubtitle>
        </div>
      </S.CardHead>
      {report.origins.length === 0 ? (
        <S.Empty>Nenhum ingresso no filtro.</S.Empty>
      ) : (
        <S.OriginList>
          {report.origins.map((origin) => (
            <S.OriginItem key={origin.origin}>
              <S.OriginName>{ORIGIN_LABELS[origin.origin]}</S.OriginName>
              <S.OriginNumbers>
                {origin.issued === null
                  ? `${formatCount(origin.entries)} entradas`
                  : `${formatCount(origin.entries)} de ${formatCount(origin.issued)} · ${formatAttendance(origin.attendance)}`}
                {origin.revenueCents !== undefined && ` · ${formatCents(origin.revenueCents)}`}
              </S.OriginNumbers>
              {origin.attendance !== null && (
                <S.OriginBar>
                  <ProgressBar
                    percentage={origin.attendance * 100}
                    track="neutral"
                    tone={attendanceTone(origin.attendance)}
                    label={`Comparecimento ${ORIGIN_LABELS[origin.origin]} ${formatAttendance(origin.attendance)}`}
                  />
                </S.OriginBar>
              )}
            </S.OriginItem>
          ))}
        </S.OriginList>
      )}
    </S.Card>
  )
}

const SCALE_OPTIONS: Array<{ value: HeatmapScale, label: string }> = [
  { value: 'row', label: 'Escala por setor' },
  { value: 'all', label: 'Escala geral' },
]

export function SectorHeatmap({ heatmap }: { heatmap: AccessReportResponse['heatmap'] }): ReactElement {
  const [scale, setScale] = useState<HeatmapScale>('row')
  const columns = useMemo(() => heatmapColumns(heatmap.hours), [heatmap.hours])
  const globalMax = heatmap.sectors.reduce((max, sector) => Math.max(max, ...sector.cells, 0), 0)
  const days = new Set(heatmap.hours.map((hour) => hour.slice(0, 10)))

  return (
    <S.Card aria-label="Ritmo por setor">
      <S.CardHead>
        <div>
          <S.CardTitle>Ritmo por setor</S.CardTitle>
          <S.CardSubtitle>Entradas por hora em cada setor. Quanto mais escura a célula, maior o fluxo.</S.CardSubtitle>
        </div>
        <S.Tools>
          <SegmentedControl ariaLabel="Escala do mapa de calor" options={SCALE_OPTIONS} value={scale} onChange={setScale} />
        </S.Tools>
      </S.CardHead>
      {heatmap.hours.length === 0 ? (
        <S.Empty>Nenhuma entrada no filtro.</S.Empty>
      ) : (
        <>
          <S.HeatmapScroll>
            <S.Heatmap>
              <thead>
                <tr>
                  <S.HeatSector scope="col">
                    <S.Hidden>Setor</S.Hidden>
                  </S.HeatSector>
                  {columns.map((column, index) =>
                    column.hour === null ? (
                      <S.HeatHeader key={`gap-${index}`} aria-hidden="true">
                        ⋯
                      </S.HeatHeader>
                    ) : (
                      <S.HeatHeader key={column.hour} scope="col">
                        {days.size > 1 ? formatDayTime(column.hour).replace(':00', 'h') : `${column.hour.slice(11, 13)}h`}
                      </S.HeatHeader>
                    )
                  )}
                  <S.HeatHeader scope="col">Total</S.HeatHeader>
                </tr>
              </thead>
              <tbody>
                {heatmap.sectors.map((sector) => {
                  const rowMax = Math.max(0, ...sector.cells)
                  return (
                    <tr key={sector.sectorId}>
                      <S.HeatSector scope="row" title={sector.sector}>
                        {sector.sector}
                      </S.HeatSector>
                      {columns.map((column, index) =>
                        column.hour === null ? (
                          <S.HeatGap key={`gap-${index}`} aria-hidden="true">
                            ⋯
                          </S.HeatGap>
                        ) : (
                          <S.HeatCell
                            key={column.hour}
                            $intensity={heatIntensity(sector.cells[column.index], rowMax, globalMax, scale)}
                            title={`${sector.sector} · ${formatDayTime(column.hour)}: ${formatCount(sector.cells[column.index])} entradas`}
                          >
                            {sector.cells[column.index] > 0 ? formatCount(sector.cells[column.index]) : ''}
                          </S.HeatCell>
                        )
                      )}
                      <S.HeatTotal>{formatCount(sector.total)}</S.HeatTotal>
                    </tr>
                  )
                })}
              </tbody>
            </S.Heatmap>
          </S.HeatmapScroll>
          <S.Legend aria-hidden="true">
            <span>menos</span>
            <S.LegendRamp />
            <span>mais entradas</span>
          </S.Legend>
        </>
      )}
    </S.Card>
  )
}

const STATUS_STATE: Record<DeviceStatus, 'on' | 'idle' | 'off' | 'end'> = {
  Lendo: 'on',
  Ocioso: 'idle',
  'Sem leitura': 'off',
  Encerrado: 'end',
  '—': 'end',
}

function StatusBadge({ status }: { status: DeviceStatus }): ReactElement {
  if (status === '—') return <S.Muted>—</S.Muted>
  return <S.Status $state={STATUS_STATE[status]}>{status}</S.Status>
}

const DEVICE_PREVIEW = 10

export function DevicesSection({ report, mobile }: { report: AccessReportResponse, mobile: boolean }): ReactElement {
  const { devices: allDevices, showValues, event, meta } = report
  const [showAll, setShowAll] = useState(false)
  const devices = showAll ? allDevices : allDevices.slice(0, DEVICE_PREVIEW)
  const statusOf = (lastAt: string): DeviceStatus => deviceStatus(lastAt, event.status, meta.now)
  const maxEntries = allDevices.reduce((max, device) => Math.max(max, device.entries), 0)

  return (
    <S.Card aria-label="Entradas por dispositivo">
      <S.CardHead>
        <div>
          <S.CardTitle>Entradas por dispositivo</S.CardTitle>
          <S.CardSubtitle>
            {allDevices.length > DEVICE_PREVIEW && !showAll
              ? `Os ${DEVICE_PREVIEW} dispositivos com mais entradas, de ${formatCount(allDevices.length)}.`
              : 'Volume, velocidade e status de cada dispositivo de leitura.'}
          </S.CardSubtitle>
        </div>
      </S.CardHead>
      {allDevices.length === 0 ? (
        <S.Empty>Nenhuma leitura no filtro.</S.Empty>
      ) : mobile ? (
        <S.MobileList>
          {devices.map((device) => (
            <S.MobileCard key={device.deviceId ?? 'none'}>
              <S.MobileCardHead>
                <S.MobileCardTitle title={device.deviceId ?? undefined}>{deviceLabel(device.deviceId)}</S.MobileCardTitle>
                <StatusBadge status={statusOf(device.lastAt)} />
              </S.MobileCardHead>
              <S.MetricGrid>
                <div>
                  <dt>Entradas</dt>
                  <dd>{`${formatCount(device.entries)} · ${formatAttendance(device.share)}`}</dd>
                </div>
                <div>
                  <dt>Média/min</dt>
                  <dd>{formatRate(device.avgPerMinute)}</dd>
                </div>
                <div>
                  <dt>Pico/min</dt>
                  <dd>{formatCount(device.peakPerMinute)}</dd>
                </div>
                <div>
                  <dt>Última leitura</dt>
                  <dd>{formatDayTime(device.lastAt)}</dd>
                </div>
                {showValues && device.revenueCents !== undefined && (
                  <div>
                    <dt>Receita</dt>
                    <dd>{formatCents(device.revenueCents)}</dd>
                  </div>
                )}
              </S.MetricGrid>
            </S.MobileCard>
          ))}
        </S.MobileList>
      ) : (
        <S.TableScroll>
          <S.Table>
            <thead>
              <tr>
                <th scope="col">Dispositivo</th>
                <th scope="col">Entradas</th>
                <th scope="col">% do total</th>
                <th scope="col">Média/min</th>
                <th scope="col">Pico/min</th>
                {showValues && <th scope="col">Receita</th>}
                <th scope="col">Última leitura</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.deviceId ?? 'none'}>
                  <td title={device.deviceId ?? undefined}>{deviceLabel(device.deviceId)}</td>
                  <td>
                    <S.BarCell>
                      <ProgressBar
                        percentage={maxEntries > 0 ? (device.entries / maxEntries) * 100 : 0}
                        track="neutral"
                        label={`Entradas de ${deviceLabel(device.deviceId)}`}
                      />
                      {formatCount(device.entries)}
                    </S.BarCell>
                  </td>
                  <td>{formatAttendance(device.share)}</td>
                  <td>{formatRate(device.avgPerMinute)}</td>
                  <td>{formatCount(device.peakPerMinute)}</td>
                  {showValues && <td>{device.revenueCents === undefined ? '—' : formatCents(device.revenueCents)}</td>}
                  <td>{event.status === 'live' ? formatClock(device.lastAt) : formatDayTime(device.lastAt)}</td>
                  <td>
                    <StatusBadge status={statusOf(device.lastAt)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </S.Table>
        </S.TableScroll>
      )}
      {allDevices.length > DEVICE_PREVIEW && (
        <S.CardFooter>
          <S.ToggleAll type="button" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
            {showAll
              ? `Mostrar só os ${DEVICE_PREVIEW} maiores`
              : `Ver todos os dispositivos (${formatCount(allDevices.length)})`}
          </S.ToggleAll>
        </S.CardFooter>
      )}
    </S.Card>
  )
}
